// RapidCaseScreen.js
// The core Rapid CCS run screen. Orchestrates the B0 -> ... state machine,
// the left (case info + results) and right (order entry) panes, the always-on
// header, physical exam, settings, rapid order lists, and the footer actions:
// Get Results, Advance Case, (and auto-grade on case end).
//
// React 16.14 compatible — class component.
//
// Props (matches the wiring sketched in RapidCcsApp.openCase):
//   caseRow           row from the selector list                  (required)
//   settings          {AutoPhysicalExam, PreventiveCareGrading}   (required)
//   onUpdateSettings  (settings) => void                          (required)
//   onOpenSettings    () => void — opens the settings modal,       (required)
//                     which is owned/rendered by RapidCcsApp
//   onExit            () => void — back to selector               (required)
//   onGraded          (gradeResult, diagnosis) => void            (required)
//   api               the api object from RapidCcsApp             (required)
//                     (every endpoint + createOrderSearch + PHYSICAL_EXAM_COMPONENTS)

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import RapidOrderEntry from "./RapidOrderEntry";
import { Modal, PhysicalExamModal, OrderListManager } from "./RapidCcsModals";

// Hardcoded rapid order list that is always pinned as the first entry.
const ROUTINE_ORDERS_LIST = {
  Name: "Routine Orders",
  Orders: [
    { Id: 687, Name: "CBC with differential" },
    { Id: 614, Name: "Comprehensive Metabolic Profile" },
    { Id: 906, Name: "EKG, 12 leave" },
    { Id: 1565, Name: "Pulse Oximetry" },
  ],
};

// Ensures the hardcoded list is first and never duplicated, regardless of
// what the server returns or what the user does in the list manager.
function withRoutineOrdersFirst(lists) {
  const rest = (Array.isArray(lists) ? lists : []).filter(function (l) {
    return l && l.Name !== ROUTINE_ORDERS_LIST.Name;
  });
  return [ROUTINE_ORDERS_LIST].concat(rest);
}

function vitalRows(vitals) {
  if (!vitals) return [];
  return [
    { label: "Temp", value: vitals.TEMPERATURE },
    { label: "Pulse", value: vitals.PULSE },
    { label: "RR", value: vitals.RESPIRATORYRATE },
    {
      label: "BP",
      value: (vitals.BPSYSTOLIC || "") + "/" + (vitals.BPDIASTOLIC || ""),
    },
    { label: "Ht", value: vitals.HEIGHT },
    { label: "Wt", value: vitals.WEIGHT },
    { label: "BMI", value: vitals.BMINDEX },
  ].filter(function (r) {
    return r.value && r.value !== "/";
  });
}

// Adjust to match the real location table.
const LOCATION_LABELS = {
  1: "Emergency Department",
  2: "Office / Clinic",
  3: "Inpatient Ward",
  4: "ICU",
};
function locationName(id) {
  return LOCATION_LABELS[id] || "Location " + id;
}

class RapidCaseScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      error: null,
      caseData: null,

      // CCS state machine value ("state" in the hooks version — renamed so it
      // doesn't collide with React's this.state).
      currentState: "B0",
      vitals: null,
      revealDiag: false,
      caseEnded: false,

      // Left-pane feed: newest first. Each entry is a result block or exam block.
      // { kind: 'result'|'exam'|'update', ... }
      feed: [],

      // Left-pane display direction. true = newest on top (original
      // behaviour); false = chronological, newest at the bottom. Display
      // only — `feed` itself is always stored newest-first.
      feedNewestFirst: true,

      // Pending orders queue on the right (not yet resulted). [{ID, PrimaryOrder}]
      queue: [],

      // Full grading sequence accumulated across the whole case (orders + exam
      // tokens interleaved in fire order). Strings.
      sequence: [],

      // Physical exam tokens already run (for manual-mode "done" display + dedupe)
      examDone: [],

      rapidLists: withRoutineOrdersFirst([]),

      busy: false,
      statusMsg: "",

      // Mobile only: which of the two panes is shown full-height ("case" or
      // "orders"). Ignored above the tablet breakpoint, where both panes show
      // side by side as usual.
      mobilePane: "case",

      // Case header: collapsing hides the secondary action row (Reveal
      // diagnosis / Order lists / Settings / feed order) down to just the
      // case meta and the End & grade button, to free up vertical space.
      headerCollapsed: false,

      // modals
      showExam: false,
      showLists: false,
      showEndConfirm: false,
    };

    // token -> display / source-field lookups for physical exam, built from
    // the metadata on the api prop (single source: RapidCcsApp).
    this.PE_DISPLAY = {};
    this.PE_FIELD = {};
    props.api.PHYSICAL_EXAM_COMPONENTS.forEach((c) => {
      this.PE_DISPLAY[c.token] = c.display;
      this.PE_FIELD[c.token] = c.field;
    });

    this._mounted = false;
    this._controller = null;

    // Ref to the left pane's scrollable container, so we can snap it to the
    // newest item whenever Get Results / Advance Case add to the feed.
    this.leftPaneScrollRef = React.createRef();
  }

  // Snaps the left pane back to its top. Deliberately defensive:
  // we don't know for certain which element actually has the overflow in the
  // stylesheet, so rather than assume it's exactly the ref'd node, we reset
  // the ref'd node AND walk up its ancestor chain resetting any scrollable
  // container we find, plus the window itself as a last resort. We also do
  // this on the next animation frame (not synchronously) so it runs after the
  // browser has actually laid out the newly-added feed content.
  scrollLeftPaneToTop = () => {
    const run = () => {
      const start = this.leftPaneScrollRef.current;
      if (!start) return;

      start.scrollTop = 0;

      let node = start.parentElement;
      while (node && node !== document.body) {
        if (node.scrollHeight > node.clientHeight) {
          node.scrollTop = 0;
        }
        node = node.parentElement;
      }

      if (typeof window !== "undefined" && window.scrollTo) {
        window.scrollTo(0, 0);
      }
    };

    // One rAF to let the DOM mutation flush, a second to let layout settle.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  };

  // Scrolls to wherever new feed content appears: the top when newest-first,
  // the bottom when chronological.
  scrollLeftPaneToNewest = () => {
    if (this.state.feedNewestFirst) {
      this.scrollLeftPaneToTop();
      return;
    }
    const run = () => {
      const el = this.leftPaneScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  };

  scrollLeftPaneToItem = (blockTs, orderId) => {
    const run = () => {
      const container = this.leftPaneScrollRef.current;
      if (!container) return;

      const target =
        orderId != null
          ? container.querySelector(
              '[data-block-ts="' +
                blockTs +
                '"][data-order-id="' +
                orderId +
                '"]',
            )
          : null;

      if (!target) {
        this.scrollLeftPaneToNewest();
        return;
      }
      target.scrollIntoView({ block: "start" });
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      run();
    }
  };

  // -------- load case (includes rapid order lists) --------
  componentDidMount() {
    this._mounted = true;
    this._controller = new AbortController();
    const settings = this.props.settings;

    this.props.api
      .startCaseRapid(this.props.caseRow.ID, this._controller.signal)
      .then((data) => {
        if (!this._mounted) return;

        const nextState = {
          caseData: data,
          vitals: data.Vitals || null,
          loading: false,
        };

        // Rapid order lists come back with the case start payload.
        // "Routine Orders" is hardcoded and always pinned first.
        if (Array.isArray(data.RapidOrderLists)) {
          nextState.rapidLists = withRoutineOrdersFirst(data.RapidOrderLists);
        }

        // If auto physical exam: drop all 12 components onto the feed immediately.
        if (settings.AutoPhysicalExam && data.PhysicalExam) {
          const examBlock = this.buildExamBlock(
            data.PhysicalExam,
            "Auto physical exam",
          );
          nextState.feed = [examBlock];
          // NOT added to grading sequence (auto exam is not graded).
        }

        this.setState(nextState);
      })
      .catch((err) => {
        if (err && err.name === "AbortError") return;
        if (!this._mounted) return;
        this.setState({
          error: "Could not start the case. Try again.",
          loading: false,
        });
      });
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._controller) this._controller.abort();
  }

  buildExamBlock = (peObject, label) => {
    const rows = this.props.api.PHYSICAL_EXAM_COMPONENTS.map(function (c) {
      const v = peObject[c.field];
      if (!v) return null;
      return { name: c.display.replace("Physical Exam: ", ""), text: v };
    }).filter(Boolean);
    return {
      kind: "exam",
      label: label,
      rows: rows,
      ts: Date.now() + Math.random(),
    };
  };

  // -------- order queue helpers --------
  addOrder = (o) => {
    this.setState(function (prevState) {
      if (
        prevState.queue.some(function (x) {
          return x.ID === o.ID;
        })
      )
        return null;
      return {
        queue: prevState.queue.concat([
          { ID: o.ID, PrimaryOrder: o.PrimaryOrder },
        ]),
      };
    });
  };

  removeOrder = (id) => {
    this.setState(function (prevState) {
      return {
        queue: prevState.queue.filter(function (x) {
          return x.ID !== id;
        }),
      };
    });
  };

  fireList = (list) => {
    // Prefer the new Orders:[{Id,Name}] shape; tolerate legacy OrderIds.
    var orders;
    if (Array.isArray(list.Orders)) {
      orders = list.Orders.map(function (o) {
        return { ID: o.Id, PrimaryOrder: o.Name || "Order #" + o.Id };
      });
    } else {
      orders = (list.OrderIds || []).map(function (id) {
        return { ID: id, PrimaryOrder: "Order #" + id };
      });
    }
    this.setState(function (prevState) {
      const have = {};
      prevState.queue.forEach(function (x) {
        have[x.ID] = true;
      });
      const additions = orders.filter(function (o) {
        return !have[o.ID];
      });
      return {
        queue: prevState.queue.concat(additions),
        statusMsg: "Added " + orders.length + " orders from " + list.Name,
      };
    });
  };

  // -------- physical exam (manual) --------
  runExam = (tokens) => {
    const caseData = this.state.caseData;
    if (!caseData || !caseData.PhysicalExam) {
      this.setState({ showExam: false });
      return;
    }

    // Build a feed block from only the selected components, in selection order.
    const rows = tokens
      .map((t) => {
        const field = this.PE_FIELD[t];
        const v = field ? caseData.PhysicalExam[field] : null;
        if (!v) return null;
        return {
          name: this.PE_DISPLAY[t].replace("Physical Exam: ", ""),
          text: v,
        };
      })
      .filter(Boolean);

    this.setState(
      function (prevState) {
        return {
          showExam: false,
          feed: [
            {
              kind: "exam",
              label: "Physical exam",
              rows: rows,
              ts: Date.now() + Math.random(),
            },
          ].concat(prevState.feed),
          // Manual exam IS graded — append tokens to the sequence in fire order.
          sequence: prevState.sequence.concat(tokens),
          examDone: prevState.examDone.concat(tokens),
          mobilePane: "case",
        };
      },
      () => this.scrollLeftPaneToNewest(),
    );
  };

  // -------- Get Results --------
  getResults = () => {
    const { queue, busy, currentState } = this.state;
    if (queue.length === 0 || busy) return;
    const orderIds = queue.map(function (x) {
      return x.ID;
    });
    const currentQueue = queue.slice();
    this.setState({ busy: true, statusMsg: "Getting results…" });

    this.props.api
      .getResultsRapid(this.props.caseRow.ID, currentState, orderIds)
      .then((results) => {
        if (!this._mounted) return;
        const list = Array.isArray(results) ? results : [];
        // Index returned results by OrderId.
        const resultById = {};
        list.forEach(function (r) {
          resultById[r.OrderId] = r;
        });

        // Build one item per ORDERED item (from the queue), so orders with no
        // lab result (IV fluids, antibiotics, medications, etc.) still move to
        // the left. Only show a result body when the backend returned text.
        // Stored newest-first (reversed queue) to match the right-hand list;
        // the chronological view flips it back at render time. (Grading
        // `sequence`/`orderIds` below intentionally stay in true
        // chronological fire order.)
        const displayQueue = currentQueue.slice().reverse();
        const items = displayQueue.map(function (q) {
          const r = resultById[q.ID];
          const text = r && r.Result != null ? String(r.Result) : "";
          return {
            orderId: q.ID,
            name: q.PrimaryOrder,
            text: text,
            hasResult: text.trim().length > 0,
          };
        });

        const block = {
          kind: "result",
          ts: Date.now(),
          items: items,
        };

        const firstOrderId = currentQueue.length ? currentQueue[0].ID : null;

        this.setState(
          function (prevState) {
            return {
              feed: [block].concat(prevState.feed),
              // Accumulate into grading sequence (order IDs as strings, fire order).
              sequence: prevState.sequence.concat(orderIds.map(String)),
              // Orders move left and clear from the right.
              queue: [],
              busy: false,
              statusMsg: "Results returned.",
              mobilePane: "case",
            };
          },
          () => this.scrollLeftPaneToItem(block.ts, firstOrderId),
        );
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({
          busy: false,
          statusMsg: "Could not get results. Try again.",
        });
      });
  };

  // -------- Advance Case --------
  advanceCase = () => {
    if (this.state.busy) return;
    // still pending must have their results fetched (getResultsRapid)
    const currentQueue = this.state.queue.slice();
    const orderIds = currentQueue.map(function (x) {
      return x.ID;
    });
    const stateForCall = this.state.currentState;
    const settings = this.props.settings;

    // The sequence progressRapidCcs needs to send, computed up front (not
    // read back from this.state after setState, since setState is async).
    const sequenceForAdvance = this.state.sequence.concat(orderIds.map(String));

    this.setState({
      busy: true,
      statusMsg: orderIds.length ? "Getting results…" : "Advancing case…",
    });

    const resultsStep = orderIds.length
      ? this.props.api.getResultsRapid(
          this.props.caseRow.ID,
          stateForCall,
          orderIds,
        )
      : Promise.resolve(null);

    // Set (if a result block ends up getting built below) so the final
    // scroll can land on the first order in this batch, same as getResults.
    let resultBlockTs = null;
    const firstOrderId = orderIds.length ? currentQueue[0].ID : null;

    resultsStep
      .then((results) => {
        if (!this._mounted) return;

        // Fold the fetched results into the feed/sequence exactly like
        // getResults does, and clear only the orders we actually resulted.
        if (orderIds.length) {
          const list = Array.isArray(results) ? results : [];
          const resultById = {};
          list.forEach(function (r) {
            resultById[r.OrderId] = r;
          });

          const displayQueue = currentQueue.slice().reverse();
          const items = displayQueue.map(function (q) {
            const r = resultById[q.ID];
            const text = r && r.Result != null ? String(r.Result) : "";
            return {
              orderId: q.ID,
              name: q.PrimaryOrder,
              text: text,
              hasResult: text.trim().length > 0,
            };
          });

          const block = {
            kind: "result",
            ts: Date.now(),
            items: items,
          };
          resultBlockTs = block.ts;

          this.setState(function (prevState) {
            return {
              feed: [block].concat(prevState.feed),
              sequence: prevState.sequence.concat(orderIds.map(String)),
              queue: prevState.queue.filter(function (x) {
                return orderIds.indexOf(x.ID) === -1;
              }),
              statusMsg: "Advancing case…",
              mobilePane: "case",
            };
          });
        }

        // Now that any pending orders are resulted, actually advance —
        // sending the full accumulated sequence (orders + exam tokens, in
        // fire order) so far, including whatever just got queued above.
        return this.props.api.progressRapidCcs(
          this.props.caseRow.ID,
          stateForCall,
          sequenceForAdvance,
        );
      })
      .then((res) => {
        if (!this._mounted) return;
        res = res || {};

        this.setState(
          function (prevState) {
            const next = {};

            // Always replace CurrentState with NewState for subsequent calls.
            if (res.NewState) next.currentState = res.NewState;

            // Replace vitals at header when provided.
            if (res.Vitals) next.vitals = res.Vitals;

            let feed = prevState.feed;

            // Patient update -> feed (newest on top).
            if (res.PATIENTUPDATE) {
              feed = [
                {
                  kind: "update",
                  text: res.PATIENTUPDATE,
                  ts: Date.now() + Math.random(),
                },
              ].concat(feed);
            }

            // Updated physical exam: only auto-add if AutoPhysicalExam is on.
            if (res.PhysicalExam && settings.AutoPhysicalExam) {
              const block = this.buildExamBlock(
                res.PhysicalExam,
                "Updated physical exam (auto)",
              );
              feed = [block].concat(feed);
            }
            if (feed !== prevState.feed) {
              next.feed = feed;
              next.mobilePane = "case";
            }

            // Store updated exam so manual re-exam reflects new findings.
            if (res.PhysicalExam) {
              next.caseData = Object.assign({}, prevState.caseData, {
                PhysicalExam: res.PhysicalExam,
              });
              // a fresh exam is available again -> allow re-running manually
              next.examDone = [];
            }

            if (res.CaseEnds) {
              next.caseEnded = true;
              if (settings.PreventiveCareGrading) {
                // Don't auto-grade. Let the user review, then grade via the header
                // button. Surface a prominent "case has ended" banner.
                next.busy = false;
                next.statusMsg =
                  "Case ended. Review, then choose End and grade case.";
              } else {
                next.statusMsg = "Case ended. Grading…";
              }
            } else {
              next.busy = false;
              next.statusMsg = "";
            }

            return next;
          },
          () => {
            if (resultBlockTs != null) {
              this.scrollLeftPaneToItem(resultBlockTs, firstOrderId);
            } else {
              // No orders were pending, so no result block was built (e.g.
              // patient update / exam blocks only).
              this.scrollLeftPaneToNewest();
            }
            // Auto-grade after state has settled so doGrade sees the final
            // sequence/queue.
            if (res.CaseEnds && !settings.PreventiveCareGrading) {
              this.doGrade();
            }
          },
        );
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({
          busy: false,
          statusMsg: "Could not advance the case. Try again.",
        });
      });
  };

  // -------- Grade (auto, on case end) --------
  doGrade = () => {
    const { queue, sequence, caseData } = this.state;
    const settings = this.props.settings;
    const caseRow = this.props.caseRow;

    // Build the final sequence. Any still-queued orders at end are folded in.
    const tail = queue.map(function (x) {
      return String(x.ID);
    });
    const finalSequence = sequence.concat(tail);

    const input = {
      CaseId: caseRow.ID,
      AutoPhysicalExam: !!settings.AutoPhysicalExam,
      PreventiveCareGrading: !!settings.PreventiveCareGrading,
      OrderSequence: finalSequence,
    };

    this.props.api
      .gradeRapidCcs(input)
      .then((grade) => {
        if (!this._mounted) return;
        this.setState({ busy: false });
        this.props.onGraded(
          grade,
          caseData ? caseData.DIAGNOSIS : caseRow.DIAGNOSIS,
        );
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({ busy: false, statusMsg: "Grading failed. Try again." });
      });
  };

  // -------- Manual "End and Grade Case" (from header) --------
  // Grading does not depend on case state, so we can grade from wherever the
  // user is. Confirm first since this ends the case early.
  confirmEndAndGrade = () => {
    this.setState(
      { showEndConfirm: false, busy: true, statusMsg: "Grading…" },
      () => {
        this.doGrade();
      },
    );
  };

  // -------- mobile pane toggle (Case & Results / Place Orders) --------
  setMobilePane = (pane) => {
    this.setState({ mobilePane: pane });
  };

  toggleHeaderCollapsed = () => {
    this.setState(function (prevState) {
      return { headerCollapsed: !prevState.headerCollapsed };
    });
  };

  // -------- feed direction toggle --------
  toggleFeedOrder = () => {
    this.setState(
      function (prevState) {
        return { feedNewestFirst: !prevState.feedNewestFirst };
      },
      () => this.scrollLeftPaneToNewest(),
    );
  };

  // -------- small modal open/close handlers --------
  openExam = () => {
    this.setState({ showExam: true });
  };
  closeExam = () => {
    this.setState({ showExam: false });
  };
  openLists = () => {
    this.setState({ showLists: true });
  };
  closeLists = () => {
    this.setState({ showLists: false });
  };
  closeEndConfirm = () => {
    this.setState({ showEndConfirm: false });
  };
  revealDiagnosis = () => {
    this.setState({ revealDiag: true });
  };

  handleEndClick = () => {
    if (this.state.caseEnded) {
      this.confirmEndAndGrade();
    } else {
      this.setState({ showEndConfirm: true });
    }
  };

  handleListsChange = (lists) => {
    if (this._mounted && Array.isArray(lists))
      this.setState({ rapidLists: withRoutineOrdersFirst(lists) });
  };

  // ----------------------------------------------------------------------
  // Left-pane render helpers
  // ----------------------------------------------------------------------

  // One feed block (result batch / exam / patient update).
  renderFeedBlock = (block, newestFirst) => {
    if (block.kind === "result") {
      // block.items is stored newest-first; flip it in chronological mode
      // so orders within a batch also read in fire order.
      const items = newestFirst ? block.items : block.items.slice().reverse();
      return (
        <div key={block.ts} data-block-ts={block.ts}>
          {items.map(function (it) {
            return (
              <div
                key={block.ts + "-" + it.orderId}
                className="rccs-result"
                data-block-ts={block.ts}
                data-order-id={it.orderId}
              >
                <div className="rccs-result__name">{it.name}</div>
                {it.hasResult ? (
                  <div className="rccs-result__body">{it.text}</div>
                ) : (
                  <div className="rccs-result__ordered">Ordered</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    if (block.kind === "exam") {
      return (
        <div className="rccs-card" key={block.ts}>
          <h4>{block.label}</h4>
          <dl className="rccs-exam-group">
            {block.rows.map(function (r, i) {
              return (
                <React.Fragment key={i}>
                  <dt>{r.name}</dt>
                  <dd>{r.text}</dd>
                </React.Fragment>
              );
            })}
          </dl>
        </div>
      );
    }
    // update
    return (
      <div className="rccs-update" key={block.ts}>
        <div className="rccs-update__label">Patient update</div>
        {block.text}
      </div>
    );
  };

  // Static case info (initial update, history, intro, starting meds).
  renderCaseInfo = () => {
    const { caseData, feed } = this.state;
    if (!caseData) return null;
    return (
      <React.Fragment>
        {caseData.PATIENTUPDATE && feed.length === 0 && (
          <div className="rccs-update">
            <div className="rccs-update__label">Patient update</div>
            {caseData.PATIENTUPDATE}
          </div>
        )}
        <div className="rccs-card">
          <h4>History</h4>
          {(caseData.HISTORY || "")
            .split(/\n\s*\n+/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((paragraph, i) => (
              <p key={i} style={{ whiteSpace: "pre-line" }}>
                {paragraph}
              </p>
            ))}
        </div>
        {caseData.INTRODUCTION && (
          <div className="rccs-card">
            <h4>Introduction</h4>
            <p>{caseData.INTRODUCTION}</p>
          </div>
        )}
        {caseData.STARTINGMED && (
          <div className="rccs-card">
            <h4>Starting medications</h4>
            <p>{caseData.STARTINGMED}</p>
          </div>
        )}
      </React.Fragment>
    );
  };

  // ----------------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------------
  render() {
    const { caseRow, settings, onExit, api } = this.props;
    const {
      loading,
      error,
      caseData,
      vitals,
      revealDiag,
      caseEnded,
      feed,
      feedNewestFirst,
      queue,
      rapidLists,
      examDone,
      busy,
      statusMsg,
      showExam,
      showLists,
      showEndConfirm,
      mobilePane,
      headerCollapsed,
    } = this.state;

    if (loading) {
      return (
        <div className="rccs">
          <div className="rccs-loading">
            <div className="rccs-spinner" />
            Starting case…
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="rccs">
          <div className="rccs-error">{error}</div>
          <div style={{ textAlign: "center" }}>
            <button className="rccs-btn" onClick={onExit}>
              Back to cases
            </button>
          </div>
        </div>
      );
    }

    const diagnosis = caseData ? caseData.DIAGNOSIS : caseRow.DIAGNOSIS;
    const vRows = vitalRows(vitals);
    const orderedFeed = feedNewestFirst ? feed : feed.slice().reverse();

    return (
      <div className="rccs rccs-main">
        <div className="rccs-case">
          {/* Header (always visible) */}
          <div
            className={
              "rccs-header" + (headerCollapsed ? " rccs-header--collapsed" : "")
            }
          >
            <div className="rccs-header__bar">
              <div className="rccs-header__meta">
                <div className="rccs-header__item">
                  <span className="rccs-header__label">Case</span>
                  <span className="rccs-header__value">#{caseRow.ID}</span>
                </div>
                <div className="rccs-header__item">
                  <span className="rccs-header__label">Location</span>
                  <span className="rccs-header__value">
                    {locationName(caseRow.CASELOCATIONID)}
                  </span>
                </div>
                {revealDiag && (
                  <div className="rccs-header__item">
                    <span className="rccs-header__label">Diagnosis</span>
                    <span className="rccs-header__value rccs-header__diag">
                      {diagnosis}
                    </span>
                  </div>
                )}
              </div>
              <div className="rccs-header__bar-actions">
                <button
                  className={
                    "rccs-btn rccs-btn--sm" +
                    (caseEnded ? " rccs-btn--primary" : " rccs-btn--outline")
                  }
                  onClick={this.handleEndClick}
                  disabled={busy}
                >
                  End &amp; grade
                </button>
                <button
                  type="button"
                  className="rccs-header__collapse-toggle"
                  onClick={this.toggleHeaderCollapsed}
                  aria-expanded={!headerCollapsed}
                  aria-label={
                    headerCollapsed
                      ? "Expand case options"
                      : "Collapse case options"
                  }
                >
                  <FontAwesomeIcon
                    icon={headerCollapsed ? faChevronDown : faChevronUp}
                  />
                </button>
              </div>
            </div>
            {!headerCollapsed && (
              <div className="rccs-header__more">
                {!revealDiag && (
                  <button
                    className="rccs-btn rccs-btn--sm rccs-btn--amber"
                    onClick={this.revealDiagnosis}
                  >
                    Reveal diagnosis
                  </button>
                )}
                <button
                  className="rccs-btn rccs-btn--sm rccs-btn--outline"
                  onClick={this.openLists}
                >
                  Order lists
                </button>
                <button
                  className="rccs-btn rccs-btn--sm rccs-btn--outline"
                  onClick={this.props.onOpenSettings}
                >
                  Settings
                </button>
                <button
                  type="button"
                  className="rccs-btn rccs-btn--sm rccs-btn--outline"
                  onClick={this.toggleFeedOrder}
                  aria-pressed={!feedNewestFirst}
                  title={
                    feedNewestFirst
                      ? "Newest results are added at the top — click to add them at the bottom"
                      : "Newest results are added at the bottom — click to add them at the top"
                  }
                >
                  <FontAwesomeIcon
                    icon={feedNewestFirst ? faArrowUp : faArrowDown}
                  />
                  {feedNewestFirst ? "Newest on top" : "Newest at bottom"}
                </button>
              </div>
            )}
          </div>

          {/* Case-ended banner (shown when the case has reached its end) */}
          {caseEnded && (
            <div className="rccs-ended-banner" role="status">
              <span className="rccs-ended-banner__title">
                The case has ended
              </span>
              <span className="rccs-ended-banner__sub">
                Review your work, then click <strong>End and grade case</strong>{" "}
                to see your grade.
              </span>
            </div>
          )}

          {/* Vitals strip */}
          {vRows.length > 0 && (
            <div className="rccs-vitals">
              {vRows.map(function (r) {
                return (
                  <span className="rccs-vital" key={r.label}>
                    {r.label}: <b>{r.value}</b>
                  </span>
                );
              })}
            </div>
          )}

          {/* Mobile-only: switch between the case pane and the order pane
              instead of splitting the screen 50/50 (see .rccs-mobile-tabs) */}
          <div
            className="rccs-mobile-tabs"
            role="tablist"
            aria-label="Case screen view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === "case"}
              className={
                "rccs-mobile-tabs__btn" +
                (mobilePane === "case" ? " rccs-mobile-tabs__btn--active" : "")
              }
              onClick={() => this.setMobilePane("case")}
            >
              Case &amp; results
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobilePane === "orders"}
              className={
                "rccs-mobile-tabs__btn" +
                (mobilePane === "orders"
                  ? " rccs-mobile-tabs__btn--active"
                  : "")
              }
              onClick={() => this.setMobilePane("orders")}
            >
              Place orders
              {queue.length > 0 && (
                <span className="rccs-mobile-tabs__badge">{queue.length}</span>
              )}
            </button>
          </div>

          {/* Body: two panes */}
          <div className="rccs-body" data-mobile-pane={mobilePane}>
            {/* LEFT: case info + results feed */}
            <div className="rccs-pane rccs-pane--left">
              <div className="rccs-pane__scroll" ref={this.leftPaneScrollRef}>
                <div className="rccs-pane__title">Case &amp; results</div>

                {/* Chronological: case info first, then oldest → newest */}
                {!feedNewestFirst && this.renderCaseInfo()}

                {orderedFeed.map((block) =>
                  this.renderFeedBlock(block, feedNewestFirst),
                )}

                {/* Newest-first: feed on top, case info at the bottom */}
                {feedNewestFirst && this.renderCaseInfo()}
              </div>
            </div>

            {/* RIGHT: order entry */}
            <div className="rccs-pane rccs-pane--right">
              <div className="rccs-pane__scroll">
                <div className="rccs-pane__title">Place orders</div>
                <RapidOrderEntry
                  api={api}
                  queue={queue}
                  onAddOrder={this.addOrder}
                  onRemoveOrder={this.removeOrder}
                  rapidLists={rapidLists}
                  onFireList={this.fireList}
                  openLists={this.openLists}
                />
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="rccs-footer">
            <span className="rccs-footer__status">
              <span className="rccs-footer__status-text">{statusMsg}</span>
              <span className="rccs-footer__count">{queue.length} queued</span>
            </span>
            {!settings.AutoPhysicalExam && (
              <button
                className="rccs-btn rccs-btn--amber"
                onClick={this.openExam}
                disabled={busy}
              >
                Order physical exam
              </button>
            )}
            <button
              className="rccs-btn rccs-btn--blue"
              onClick={this.getResults}
              disabled={busy || queue.length === 0}
            >
              Get results
            </button>
            <button
              className="rccs-btn rccs-btn--primary"
              onClick={this.advanceCase}
              disabled={busy || caseEnded}
              title={caseEnded ? "The case has ended" : undefined}
            >
              Advance case
            </button>
          </div>
        </div>

        {/* Modals */}
        {showExam && (
          <PhysicalExamModal
            api={api}
            done={examDone}
            onClose={this.closeExam}
            onRun={this.runExam}
          />
        )}
        {showLists && (
          <OrderListManager
            api={api}
            initialLists={rapidLists}
            onClose={this.closeLists}
            onChange={this.handleListsChange}
          />
        )}
        {showEndConfirm && (
          <Modal
            title="End and grade case?"
            onClose={this.closeEndConfirm}
            footer={
              <React.Fragment>
                <button className="rccs-btn" onClick={this.closeEndConfirm}>
                  Keep working
                </button>
                <button
                  className="rccs-btn rccs-btn--primary"
                  onClick={this.confirmEndAndGrade}
                >
                  End and grade now
                </button>
              </React.Fragment>
            }
          >
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>
              This will end the case now — without finishing it normally — and
              grade everything you've ordered so far. You can't continue this
              attempt afterward. Grade now?
            </p>
          </Modal>
        )}
      </div>
    );
  }
}

export default RapidCaseScreen;
